import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import axios from 'axios';
import { logError } from '../../utils/logger';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = n => `$${Number(n||0).toLocaleString('en-US',{minimumFractionDigits:0})}`;

export default function DeductionGapsPanel({ year }) {
  const { t } = useTranslation();
  const [data, setData] = useState(null);

  useEffect(() => {
    const params = year ? `?year=${year}` : '';
    axios.get(`${API}/deductions/gaps${params}`, { withCredentials: true })
      .then(r => setData(r.data))
      .catch(e => logError(e, { context: 'DeductionGapsPanel' }));
  }, [year]);

  if (!data || data.gap_count === 0) return null;

  return (
    <Card className="border-amber-200 bg-amber-50" data-testid="deduction-gaps-panel">
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          {t('deductions.gaps_title', 'Potential Missed Deductions')}
          <Badge className="bg-amber-100 text-amber-700 text-xs">{data.gap_count}</Badge>
        </CardTitle>
        <p className="text-xs text-amber-700">
          {t('deductions.profession_label', 'Based on your profession')}: <strong>{data.profession}</strong>
          {' · '}{t('deductions.potential_missed', 'Potential annual value')}: <strong>{fmt(data.total_missed_estimate)}</strong>
        </p>
      </CardHeader>
      <CardContent className="space-y-3">
        {(data.gaps || []).map(gap => (
          <div key={gap.category} className="flex justify-between items-start bg-white rounded-lg p-3 border border-amber-100">
            <div>
              <p className="font-semibold text-sm text-gray-900 capitalize">{gap.category.replace(/_/g,' ')}</p>
              <p className="text-xs text-gray-600 mt-0.5">{gap.description}</p>
              <p className="text-xs text-muted-foreground">Schedule C Line {gap.schedule_c_line}</p>
            </div>
            <div className="text-right flex-shrink-0 ml-3">
              <p className="text-sm font-bold text-amber-700">{fmt(gap.typical_annual)}/yr</p>
              <Badge className={`text-xs ${gap.priority === 'high' ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                {gap.priority}
              </Badge>
            </div>
          </div>
        ))}
        <p className="text-xs text-amber-600">{data.disclaimer}</p>
      </CardContent>
    </Card>
  );
}
